import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, Send, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { COMPANY, CONTACT_SERVICES, BUDGET_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  service: "",
  budget: "",
  message: "",
};

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-electric focus:ring-4 focus:ring-electric/10";

const ContactInfoRow = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex items-start gap-4">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-electric/10">
      <Icon className="h-5 w-5 text-electric" strokeWidth={1.5} />
    </div>
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
      {href ? (
        <a
          href={href}
          data-testid={testId}
          className="mt-1 block text-sm font-medium text-slate-800 transition-colors duration-300 hover:text-electric"
        >
          {value}
        </a>
      ) : (
        <p className="mt-1 text-sm font-medium text-slate-800" data-testid={testId}>
          {value}
        </p>
      )}
    </div>
  </div>
);

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select a service.";
    if (form.message.trim().length < 10) er.message = "Please tell us a little more (min 10 characters).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service,
        budget: form.budget || null,
        message: form.message.trim(),
      });
      toast.success("Enquiry sent. Our team will get back to you shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const FieldError = ({ id }) =>
    errors[id] ? (
      <p data-testid={`error-${id}`} className="mt-1.5 text-xs text-red-600" role="alert">
        {errors[id]}
      </p>
    ) : null;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-24 bg-slate-50 py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHeading
              overline="Contact"
              title="Start The Conversation"
              text="Tell us about your business and what you'd like to achieve. We'll respond with clear next steps — no obligation."
            />
            <Reveal delay={0.1}>
              <div className="mt-12 space-y-7">
                <p className="font-display text-lg font-medium text-slate-900">{COMPANY.name}</p>
                <ContactInfoRow
                  icon={Mail}
                  label="Email"
                  value={COMPANY.email}
                  href={`mailto:${COMPANY.email}`}
                  testId="contact-email"
                />
                <ContactInfoRow
                  icon={Phone}
                  label="Phone"
                  value={COMPANY.phone}
                  href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}
                  testId="contact-phone"
                />
                <ContactInfoRow
                  icon={MapPin}
                  label="Address"
                  value={COMPANY.address}
                  testId="contact-address"
                />
                <ContactInfoRow
                  icon={Globe}
                  label="Website"
                  value={COMPANY.website}
                  href={COMPANY.websiteUrl}
                  testId="contact-website"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              noValidate
              className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60 md:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="full_name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Full Name *
                  </label>
                  <input id="full_name" data-testid="input-full-name" className={inputClass} value={form.full_name} onChange={set("full_name")} placeholder="Your name" autoComplete="name" />
                  <FieldError id="full_name" />
                </div>
                <div>
                  <label htmlFor="business_name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Business Name
                  </label>
                  <input id="business_name" data-testid="input-business-name" className={inputClass} value={form.business_name} onChange={set("business_name")} placeholder="Your business" autoComplete="organization" />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Email Address *
                  </label>
                  <input id="email" type="email" data-testid="input-email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@company.com" autoComplete="email" />
                  <FieldError id="email" />
                </div>
                <div>
                  <label htmlFor="phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Phone Number
                  </label>
                  <input id="phone" type="tel" data-testid="input-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+44 ..." autoComplete="tel" />
                </div>
                <div>
                  <label htmlFor="service" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Service Required *
                  </label>
                  <select id="service" data-testid="select-service" className={inputClass} value={form.service} onChange={set("service")}>
                    <option value="">Select a service</option>
                    {CONTACT_SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <FieldError id="service" />
                </div>
                <div>
                  <label htmlFor="budget" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Project Budget
                  </label>
                  <select id="budget" data-testid="select-budget" className={inputClass} value={form.budget} onChange={set("budget")}>
                    <option value="">Select a range</option>
                    {BUDGET_OPTIONS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Message *
                  </label>
                  <textarea id="message" rows={5} data-testid="input-message" className={`${inputClass} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your project, challenge or idea..." />
                  <FieldError id="message" />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="send-enquiry-btn"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 font-medium text-white transition-[background-color,box-shadow,transform,opacity] duration-300 hover:scale-[1.01] hover:bg-electric-bright hover:shadow-[0_0_28px_rgba(37,99,235,0.4)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
